// src/pages/Home.js

import { useState, useEffect } from 'react';
import TypingText from '../components/TypingText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin, faGithub } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faLocationDot, faChevronDown } from '@fortawesome/free-solid-svg-icons';

function Home() {
    const [activeSection, setActiveSection] = useState('home');

    useEffect(() => {
        const handleScroll = () => {
            const sections = ['home', 'projects', 'contact'];
            const scrollPosition = window.scrollY + window.innerHeight / 2;

            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                        // If we're in the projects section, check if we're in the skills subsection
                        if (section === 'projects') {
                            const skillsElement = document.getElementById('skills');
                            if (skillsElement) {
                                const skillsTop = skillsElement.offsetTop;
                                if (window.scrollY + window.innerHeight / 2 >= skillsTop) {
                                    setActiveSection('skills');
                                    break;
                                }
                            }
                        }
                        setActiveSection(section);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Pass activeSection to window for Navbar to access
    useEffect(() => {
        window.activeSection = activeSection;
        window.dispatchEvent(new Event('activeSectionChange'));
    }, [activeSection]);
    const roles = ['Financial Data Scientist', 'Full-Stack Developer', 'ML Engineer'];

    const projects = [
        {
            id: 1,
            title: 'Stock Volatility Forecasting Dashboard',
            description: 'Dashboard for stock analysis, forecasting stock volatility and forecasting stock prices using OLS regression, ARIMA, and GARCH modeling. Made with python, streamlit, yfinance.',
            image: '/images/stockvol.png',
            tags: ['Python', 'Streamlit', 'Pandas', 'Streamlit'],
            demoUrl: null,
            githubUrl: 'https://github.com/charles-cetta/Stock_Volatility_Dashboard'
        },
        {
            id: 2,
            title: 'WalletWise Finance Tracking Dashboard',
            description: 'Full-Stack Finance and Budgeting Web Application, providing an analysis of bank statement data using React, python, and custom ML models',
            image: '/images/WalletWise.jpeg',
            tags: ['React', 'MongoDB', 'API', 'Python'],
            demoUrl: null,
            githubUrl: 'https://github.com/charles-cetta/WalletWise'
        },
        {
            id: 3,
            title: 'Credit Risk Model',
            description: 'Classification model to assess loan default probability using ensemble methods and feature engineering.',
            image: '/images/project3.png',
            tags: ['Python', 'Scikit-learn', 'SQL'],
            demoUrl: null,
            githubUrl: 'https://github.com/yourusername/project3'
        },
        {
            id: 4,
            title: 'Find Your Vote NJ',
            description: 'An interactive web application that helps New Jersey residents find information about their congressional districts, representatives, and upcoming elections. Using Azure SQL and React.',
            image: '/images/findvote.png',
            tags: ['React', 'Javascript', 'Azure SQL'],
            demoUrl: null,
            githubUrl: 'https://github.com/charles-cetta/findyourvote-nj'
        }
    ];

    const skillCategories = [
        {
            category: 'Data Science & ML',
            skills: ['Python', 'Pandas', 'NumPy', 'Scikit-learn', 'TensorFlow', 'PyTorch', 'Statistical Modeling'],
            bgColor: '#EBF4FF',
            borderColor: '#BEE3F8'
        },
        {
            category: 'Financial Analysis',
            skills: ['Time Series Analysis', 'Risk Modeling', 'Portfolio Optimization', 'Volatility Forecasting', 'Quantitative Analysis'],
            bgColor: '#EBF4FF',
            borderColor: '#BEE3F8'
        },
        {
            category: 'Development',
            skills: ['React', 'JavaScript', 'Node.js', 'SQL', 'MongoDB', 'Azure', 'Git'],
            bgColor: '#EBF4FF',
            borderColor: '#BEE3F8'
        },
        {
            category: 'Tools & Frameworks',
            skills: ['Streamlit', 'Jupyter', 'Excel', 'Power BI', 'API Development'],
            bgColor: '#EBF4FF',
            borderColor: '#BEE3F8'
        }
    ];

    const education = [
        {
            degree: 'Master of Science in Financial Data Science',
            school: 'University College Dublin Smurfit Graduate Business School',
            period: '2025 - 2026',
            description: 'Focused on quantitative finance, machine learning, and statistical modeling. Relevant coursework includes Time Series Analysis, Portfolio Theory, Risk Management, and Deep Learning.',
            logo: '/images/ucd_logo.jpeg'
        },
        {
            degree: 'Bachelor of Science in Computer Science/Cybersecurity',
            school: 'Kean University',
            period: '2021 - 2025',
            description: 'Strong foundation in algorithms, data structures, high-performance computing, distributed systems',
            logo: '/images/kean_logo.jpeg'
        }
    ];

    // Technology-specific colors for Development skills
    const getTechColor = (skill) => {
        const techColors = {
            'React': { bg: '#61DAFB', color: '#282C34' },
            'JavaScript': { bg: '#F7DF1E', color: '#000000' },
            'Node.js': { bg: '#68A063', color: '#FFFFFF' },
            'SQL': { bg: '#F29111', color: '#FFFFFF' },
            'MongoDB': { bg: '#00ED64', color: '#001E2B' },
            'Azure': { bg: '#0078D4', color: '#FFFFFF' },
            'Git': { bg: '#F05032', color: '#FFFFFF' }
        };
        return techColors[skill] || { bg: '#E8E8E8', color: '#2D3748' };
    };

    // Contact form state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    const [formStatus, setFormStatus] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormStatus('Sending...');

        try {
            // Create form data
            const data = new FormData();
            data.append('name', formData.name);
            data.append('email', formData.email);
            data.append('subject', formData.subject);
            data.append('message', formData.message);

            // Send to Cloudflare Pages function
            const response = await fetch('/contact', {
                method: 'POST',
                body: data
            });

            const result = await response.json();

            if (result.success) {
                setFormStatus(result.message);
                setFormData({ name: '', email: '', subject: '', message: '' });
            } else {
                setFormStatus(result.message || 'Failed to send message. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setFormStatus('Failed to send message. Please try emailing me directly at cetta.charles.contact@gmail.com');
        }

        setTimeout(() => setFormStatus(''), 8000);
    };

    const contactInfo = [
        {
            icon: faEnvelope,
            label: 'Email',
            value: 'cetta.charles.contact@gmail.com',
            link: 'mailto:cetta.charles.contact@gmail.com'
        },
        {
            icon: faLinkedin,
            label: 'LinkedIn',
            value: 'Connect with me',
            link: 'https://www.linkedin.com/in/charles-cetta-44b620228/'
        },
        {
            icon: faGithub,
            label: 'GitHub',
            value: 'View my work',
            link: 'https://github.com/charles-cetta'
        },
        {
            icon: faLocationDot,
            label: 'Location',
            value: 'New York City Area',
            link: null
        }
    ];

    // Scroll indicator component
    const ScrollIndicator = ({ targetSection }) => (
        <a
            href={`#${targetSection}`}
            style={{
                position: 'absolute',
                bottom: '2rem',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                textDecoration: 'none',
                color: '#2B6CB0',
                animation: 'bounce 2s infinite',
                cursor: 'pointer',
                zIndex: 10
            }}
        >
            <style>{`
                @keyframes bounce {
                    0%, 20%, 50%, 80%, 100% { transform: translateX(-50%) translateY(0); }
                    40% { transform: translateX(-50%) translateY(-10px); }
                    60% { transform: translateX(-50%) translateY(-5px); }
                }
            `}</style>
            <FontAwesomeIcon
                icon={faChevronDown}
                style={{
                    fontSize: '2rem',
                    opacity: 0.7,
                    transition: 'opacity 0.3s'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.7'}
            />
        </a>
    );

    return (
        <div>
            <style>{`
                @media (max-width: 768px) {
                    .project-grid { grid-template-columns: 1fr !important; }
                    .skills-grid { grid-template-columns: 1fr !important; }
                    .contact-grid { grid-template-columns: 1fr !important; }
                    .education-card { flex-direction: column !important; text-align: center; }
                    .education-logo { margin: 0 auto 1rem auto !important; }
                }
                @media (max-width: 480px) {
                    .hero-buttons { flex-direction: column; width: 100%; }
                    .hero-buttons a { width: 100%; text-align: center; }
                }
            `}</style>

            {/* Hero Section */}
            <section id="home" style={{
                minHeight: '85vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'clamp(2rem, 5vh, 3rem) clamp(1rem, 5vw, 2rem)',
                position: 'relative'
            }}>
                <div style={{ maxWidth: '800px', textAlign: 'center', width: '100%' }}>
                    <h1 style={{
                        fontSize: 'clamp(2rem, 5vw, 3rem)',
                        marginBottom: '1rem',
                        lineHeight: '1.2'
                    }}>
                        Hi, I'm Charles Cetta
                    </h1>
                    <p style={{
                        fontSize: 'clamp(1.1rem, 3vw, 1.5rem)',
                        color: '#4A5568',
                        marginBottom: '0.5rem',
                        lineHeight: '1.4'
                    }}>
                        I am a <TypingText words={roles}
                                           typingSpeed={120}
                                           deletingSpeed={60}
                                           pauseTime={2000}/>
                    </p>
                    <p style={{
                        fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)',
                        color: '#718096',
                        maxWidth: '700px',
                        margin: '1.5rem auto',
                        lineHeight: '1.6',
                        padding: '0 1rem'
                    }}>
                        Financial data science graduate student specializing in machine learning,
                        quantitative analysis, and full-stack development.
                    </p>
                    <div className="hero-buttons" style={{
                        marginTop: '2.5rem',
                        display: 'flex',
                        gap: '1rem',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        padding: '0 1rem'
                    }}>
                        <a
                            href="/assets/Charles_Cetta_Resume.pdf"
                            style={{
                                padding: 'clamp(0.75rem, 2vw, 0.875rem) clamp(1.5rem, 4vw, 2rem)',
                                backgroundColor: '#2B6CB0',
                                color: 'white',
                                textDecoration: 'none',
                                borderRadius: '5px',
                                fontWeight: '500',
                                fontSize: 'clamp(0.95rem, 2.5vw, 1.05rem)',
                                transition: 'background-color 0.2s',
                                minHeight: '44px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#2C5282'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#2B6CB0'}
                        >
                            Download Resume
                        </a>
                        <a
                            href="#projects"
                            style={{
                                padding: 'clamp(0.75rem, 2vw, 0.875rem) clamp(1.5rem, 4vw, 2rem)',
                                border: '2px solid #2B6CB0',
                                color: '#2B6CB0',
                                textDecoration: 'none',
                                borderRadius: '5px',
                                fontWeight: '500',
                                fontSize: 'clamp(0.95rem, 2.5vw, 1.05rem)',
                                transition: 'all 0.2s',
                                minHeight: '44px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#2B6CB0';
                                e.target.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#2B6CB0';
                            }}
                        >
                            View Projects
                        </a>
                    </div>
                </div>
                <ScrollIndicator targetSection="projects" />
            </section>

            {/* Projects & Skills Combined Section */}
            <section id="projects" style={{
                padding: 'clamp(2rem, 5vw, 4rem) clamp(1rem, 5vw, 2rem)',
                backgroundColor: '#FFFFFF',
                position: 'relative'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    {/* Projects */}
                    <h2 style={{
                        fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                        marginBottom: '1rem',
                        textAlign: 'center'
                    }}>
                        Projects
                    </h2>
                    <p style={{
                        color: '#718096',
                        marginBottom: '3rem',
                        textAlign: 'center',
                        fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                        padding: '0 1rem'
                    }}>
                        A selection of my data science and development work
                    </p>

                    <div className="project-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: 'clamp(1.5rem, 3vw, 2rem)',
                        marginBottom: 'clamp(3rem, 5vw, 5rem)'
                    }}>
                        {projects.map((project) => (
                            <div
                                key={project.id}
                                style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '2px solid #E2E8F0',
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-8px)';
                                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(44, 82, 130, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    style={{
                                        width: '100%',
                                        height: '200px',
                                        objectFit: 'cover',
                                        backgroundColor: '#EBF4FF'
                                    }}
                                />
                                <div style={{ padding: '1.5rem' }}>
                                    <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1.3rem' }}>{project.title}</h3>
                                    <p style={{ color: '#718096', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '1rem' }}>
                                        {project.description}
                                    </p>

                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                                        {project.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                style={{
                                                    backgroundColor: '#EBF4FF',
                                                    padding: '0.3rem 0.8rem',
                                                    borderRadius: '50px',
                                                    fontSize: '0.85rem',
                                                    color: '#2B6CB0',
                                                    border: '1px solid #E2E8F0',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>

                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        {project.demoUrl && (
                                            <a
                                                href={project.demoUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#2B6CB0', fontSize: '0.95rem', fontWeight: '500', textDecoration: 'none' }}
                                            >
                                                Live Demo →
                                            </a>
                                        )}
                                        {project.githubUrl && (
                                            <a
                                                href={project.githubUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#2C5282', fontSize: '0.95rem', fontWeight: '500', textDecoration: 'none' }}
                                            >
                                                GitHub →
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Skills */}
                    <div id="skills" style={{ marginTop: 'clamp(2rem, 4vw, 4rem)' }}>
                        <h2 style={{
                            fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                            marginBottom: '1rem',
                            textAlign: 'center'
                        }}>
                            Technical Skills
                        </h2>
                        <p style={{
                            color: '#718096',
                            marginBottom: '3rem',
                            textAlign: 'center',
                            fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                            padding: '0 1rem'
                        }}>
                            Tools and technologies I work with
                        </p>

                        <div className="skills-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: 'clamp(1.5rem, 3vw, 2rem)',
                            marginBottom: 'clamp(2rem, 4vw, 4rem)'
                        }}>
                            {skillCategories.map((category) => (
                                <div key={category.category} style={{
                                    padding: '1.75rem',
                                    backgroundColor: category.bgColor,
                                    borderRadius: '12px',
                                    border: `3px solid ${category.borderColor}`,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    transition: 'transform 0.2s',
                                    cursor: 'default'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                >
                                    <h3 style={{
                                        margin: '0 0 1.25rem 0',
                                        fontSize: '1.25rem',
                                        fontWeight: '600'
                                    }}>
                                        {category.category}
                                    </h3>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                                        {category.skills.map((skill) => {
                                            const isDevSkill = category.category === 'Development';
                                            const techColor = isDevSkill ? getTechColor(skill) : null;

                                            return (
                                                <span
                                                    key={skill}
                                                    style={{
                                                        backgroundColor: techColor ? techColor.bg : 'rgba(255, 255, 255, 0.9)',
                                                        padding: '0.5rem 1rem',
                                                        borderRadius: '6px',
                                                        fontSize: '0.9rem',
                                                        color: techColor ? techColor.color : '#333',
                                                        border: techColor ? 'none' : '1px solid rgba(0,0,0,0.15)',
                                                        fontWeight: techColor ? '600' : '500',
                                                        boxShadow: techColor ? '0 2px 4px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.1)'
                                                    }}
                                                >
                                                    {skill}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Education Section */}
                        <div style={{ marginTop: 'clamp(2rem, 4vw, 4rem)' }}>
                            <h2 style={{
                                fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                                marginBottom: '1rem',
                                textAlign: 'center'
                            }}>
                                Education
                            </h2>
                            <p style={{
                                color: '#718096',
                                marginBottom: '3rem',
                                textAlign: 'center',
                                fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                                padding: '0 1rem'
                            }}>
                                My academic background
                            </p>

                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'clamp(1.5rem, 3vw, 1.75rem)',
                                maxWidth: '900px',
                                margin: '0 auto'
                            }}>
                                {education.map((edu, index) => (
                                    <div
                                        key={index}
                                        className="education-card"
                                        style={{
                                            padding: 'clamp(1.25rem, 3vw, 1.75rem)',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '12px',
                                            border: '3px solid #E2E8F0',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                            transition: 'transform 0.2s, box-shadow 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-4px)';
                                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(44, 82, 130, 0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem' }}>
                                            <img
                                                className="education-logo"
                                                src={edu.logo}
                                                alt={`${edu.school} logo`}
                                                style={{
                                                    width: 'clamp(60px, 10vw, 70px)',
                                                    height: 'clamp(60px, 10vw, 70px)',
                                                    objectFit: 'contain',
                                                    flexShrink: 0,
                                                    backgroundColor: '#EBF4FF',
                                                    padding: '0.65rem',
                                                    borderRadius: '10px',
                                                    border: '2px solid #E2E8F0'
                                                }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <h3 style={{
                                                    margin: '0 0 0.65rem 0',
                                                    fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                                                    color: '#1A365D',
                                                    lineHeight: '1.3'
                                                }}>
                                                    {edu.degree}
                                                </h3>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: '0.85rem',
                                                    flexWrap: 'wrap',
                                                    gap: '0.5rem'
                                                }}>
                                                    <p style={{
                                                        margin: 0,
                                                        color: '#2B6CB0',
                                                        fontWeight: '600',
                                                        fontSize: 'clamp(0.95rem, 2.5vw, 1.05rem)'
                                                    }}>
                                                        {edu.school}
                                                    </p>
                                                    <p style={{
                                                        margin: 0,
                                                        color: '#718096',
                                                        fontSize: 'clamp(0.9rem, 2vw, 0.95rem)',
                                                        fontWeight: '500'
                                                    }}>
                                                        {edu.period}
                                                    </p>
                                                </div>
                                                <p style={{
                                                    margin: 0,
                                                    color: '#718096',
                                                    lineHeight: '1.7',
                                                    fontSize: 'clamp(0.9rem, 2vw, 0.95rem)'
                                                }}>
                                                    {edu.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <ScrollIndicator targetSection="contact" />
            </section>

            {/* Contact Section */}
            <section id="contact" style={{
                padding: 'clamp(2rem, 5vw, 4rem) clamp(1rem, 5vw, 2rem) clamp(2rem, 4vw, 3rem) clamp(1rem, 5vw, 2rem)',
                backgroundColor: '#F7FAFC'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                        <h2 style={{
                            fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                            marginBottom: '1rem'
                        }}>
                            Get In Touch
                        </h2>
                        <p style={{
                            fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                            color: '#718096',
                            maxWidth: '600px',
                            margin: '0 auto',
                            padding: '0 1rem',
                            lineHeight: '1.6'
                        }}>
                            I'm always interested in new opportunities, collaborations, or just chatting about
                            data science and finance. Feel free to reach out!
                        </p>
                    </div>

                    <div className="contact-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: 'clamp(2rem, 4vw, 3rem)',
                        marginBottom: 'clamp(2rem, 4vw, 4rem)'
                    }}>
                        {/* Contact Information */}
                        <div>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>
                                Contact Information
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                {contactInfo.map((info, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '1rem',
                                            padding: '1.25rem',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '8px',
                                            border: '2px solid #E2E8F0',
                                            transition: 'box-shadow 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(44, 82, 130, 0.1)'}
                                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                                    >
                                        <FontAwesomeIcon
                                            icon={info.icon}
                                            style={{
                                                fontSize: '1.4rem',
                                                color: '#2B6CB0',
                                                marginTop: '0.2rem'
                                            }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <p style={{
                                                margin: '0 0 0.25rem 0',
                                                fontWeight: '600',
                                                color: '#2D3748',
                                                fontSize: '1rem'
                                            }}>
                                                {info.label}
                                            </p>
                                            {info.link ? (
                                                <a
                                                    href={info.link}
                                                    target={info.link.startsWith('http') ? '_blank' : undefined}
                                                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                                                    style={{
                                                        color: '#2B6CB0',
                                                        textDecoration: 'none',
                                                        fontSize: '0.95rem'
                                                    }}
                                                >
                                                    {info.value}
                                                </a>
                                            ) : (
                                                <p style={{ margin: 0, color: '#718096', fontSize: '0.95rem' }}>
                                                    {info.value}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>
                                Send a Message
                            </h3>
                            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                <div>
                                    <label
                                        htmlFor="name"
                                        style={{
                                            display: 'block',
                                            marginBottom: '0.5rem',
                                            fontWeight: '500',
                                            color: '#2D3748',
                                            fontSize: '0.95rem'
                                        }}
                                    >
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: 'clamp(0.75rem, 2vw, 0.875rem)',
                                            border: '2px solid #E2E8F0',
                                            borderRadius: '6px',
                                            fontSize: 'clamp(0.95rem, 2vw, 1rem)',
                                            fontFamily: 'inherit',
                                            boxSizing: 'border-box',
                                            transition: 'border-color 0.2s',
                                            minHeight: '44px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#2B6CB0'}
                                        onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="email"
                                        style={{
                                            display: 'block',
                                            marginBottom: '0.5rem',
                                            fontWeight: '500',
                                            color: '#2D3748',
                                            fontSize: '0.95rem'
                                        }}
                                    >
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: 'clamp(0.75rem, 2vw, 0.875rem)',
                                            border: '2px solid #E2E8F0',
                                            borderRadius: '6px',
                                            fontSize: 'clamp(0.95rem, 2vw, 1rem)',
                                            fontFamily: 'inherit',
                                            boxSizing: 'border-box',
                                            transition: 'border-color 0.2s',
                                            minHeight: '44px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#2B6CB0'}
                                        onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="subject"
                                        style={{
                                            display: 'block',
                                            marginBottom: '0.5rem',
                                            fontWeight: '500',
                                            color: '#2D3748',
                                            fontSize: '0.95rem'
                                        }}
                                    >
                                        Subject *
                                    </label>
                                    <input
                                        type="text"
                                        id="subject"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: 'clamp(0.75rem, 2vw, 0.875rem)',
                                            border: '2px solid #E2E8F0',
                                            borderRadius: '6px',
                                            fontSize: 'clamp(0.95rem, 2vw, 1rem)',
                                            fontFamily: 'inherit',
                                            boxSizing: 'border-box',
                                            transition: 'border-color 0.2s',
                                            minHeight: '44px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#2B6CB0'}
                                        onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="message"
                                        style={{
                                            display: 'block',
                                            marginBottom: '0.5rem',
                                            fontWeight: '500',
                                            color: '#2D3748',
                                            fontSize: '0.95rem'
                                        }}
                                    >
                                        Message *
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows="5"
                                        style={{
                                            width: '100%',
                                            padding: 'clamp(0.75rem, 2vw, 0.875rem)',
                                            border: '2px solid #E2E8F0',
                                            borderRadius: '6px',
                                            fontSize: 'clamp(0.95rem, 2vw, 1rem)',
                                            fontFamily: 'inherit',
                                            resize: 'vertical',
                                            boxSizing: 'border-box',
                                            transition: 'border-color 0.2s',
                                            minHeight: '120px'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#2B6CB0'}
                                        onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    style={{
                                        padding: 'clamp(0.875rem, 2vw, 1rem) clamp(1.5rem, 4vw, 2rem)',
                                        backgroundColor: '#2B6CB0',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: 'clamp(0.95rem, 2vw, 1rem)',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s',
                                        fontFamily: 'inherit',
                                        minHeight: '44px',
                                        width: '100%'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2C5282'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#2B6CB0'}
                                >
                                    Send Message
                                </button>

                                {formStatus && (
                                    <div style={{
                                        padding: '1rem',
                                        backgroundColor: '#d4f4dd',
                                        border: '2px solid #8FD9A8',
                                        borderRadius: '6px',
                                        color: '#1e7e34',
                                        fontSize: '0.95rem',
                                        fontWeight: '500'
                                    }}>
                                        {formStatus}
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>

                    {/* Map Section */}
                    <div>
                        <h3 style={{
                            fontSize: 'clamp(1.3rem, 3vw, 1.5rem)',
                            marginBottom: '1.5rem',
                            textAlign: 'center'
                        }}>
                            Location
                        </h3>
                        <div style={{
                            border: '3px solid #E2E8F0',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 16px rgba(44, 82, 130, 0.15)',
                            maxWidth: '100%'
                        }}>
                            <iframe
                                title="Location Map"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d387190.2798904705!2d-74.25986548248684!3d40.69767006338158!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY!5e0!3m2!1sen!2sus!4v1234567890123"
                                width="100%"
                                height="clamp(300px, 50vw, 400px)"
                                style={{ border: 0, display: 'block' }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                        <p style={{
                            textAlign: 'center',
                            marginTop: '1rem',
                            color: '#718096',
                            fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                            padding: '0 1rem'
                        }}>
                            Based in the New York City metropolitan area
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Home;